import { useEffect, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';
import type { BookEdits } from './actions';
import type { BookData } from './types';
import { todayIn } from './model';

// Recent changes (Kristi, Oct 27): every change to this book from the last day, in plain words,
// each with its own Undo, for as long as the database keeps it (a day). The notice's Undo is
// only there for a few seconds; this is the way back after that.

type Action = { id: string; summary: string; made_at: string; undone_at: string | null; what?: string };

const FORMATS: Record<string, string> = {
  kindle: 'Kindle',
  audible: 'Audible',
  paper: 'Paper',
  apple_books: 'Apple Books',
  librofm: 'Libro.fm',
  libby: 'Libby',
  pdf_epub: 'PDF / EPUB',
  other: 'Other',
};

/** "3:45 pm", or "yesterday 9:12 pm". */
export function whenText(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' }).toLowerCase();
  const day = (x: Date) => todayIn('America/Los_Angeles', x);
  return day(d) === day(now) ? time : `yesterday ${time}`;
}

/** The last day's changes to the book; `refresh` after an Undo. */
export function useRecentChanges(book: BookData): { actions: Action[] | null; refresh: () => void } {
  const [actions, setActions] = useState<Action[] | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!supabase) return;
    let live = true;
    const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    void (async () => {
      const { data } = await supabase!
        .from('book_actions')
        .select('id, summary, made_at, undone_at')
        .eq('book_id', book.id)
        .gte('made_at', since)
        .order('made_at', { ascending: false })
        .limit(30);
      const list = (data ?? []) as Action[];
      // Which copy a copy change was about ("Audible copy removed").
      const copyActs = list.filter((a) => /^Copy /.test(a.summary)).map((a) => a.id);
      if (copyActs.length) {
        const { data: ch } = await supabase!.from('book_action_changes').select('action_id, tbl, before, after').in('action_id', copyActs).eq('tbl', 'copies');
        for (const c of (ch ?? []) as { action_id: string; before: { format?: string } | null; after: { format?: string } | null }[]) {
          const a = list.find((x) => x.id === c.action_id);
          const f = c.before?.format ?? c.after?.format;
          if (a && f && !a.what) a.what = `${FORMATS[f] ?? f} copy ${a.summary.replace(/^Copy /, '')}`;
        }
      }
      if (live) setActions(list);
    })();
    return () => {
      live = false;
    };
  }, [book, tick]);
  return { actions, refresh: () => setTick(tick + 1) };
}

/** Recent changes, inside its fold on the book page: each with its own Undo. */
export function RecentChanges({ actions, refresh, edits }: { actions: Action[]; refresh: () => void; edits: BookEdits }) {
  return (
    <div class="bk-recent">
      <p class="bk-quiet">Your changes to this book from the last day. Each can be undone until it’s a day old.</p>
      <ol class="bk-log">
        {actions.map((a) => (
          <li key={a.id}>
            <span class="bk-log-date">{whenText(a.made_at)}</span>
            <span class="bk-log-text">{a.what ?? a.summary}</span>
            <span>
              {a.undone_at ? (
                <span class="bk-quiet">undone</span>
              ) : (
                <button
                  type="button"
                  class="btn-link"
                  aria-label={`Undo: ${a.what ?? a.summary}`}
                  onClick={async () => {
                    await edits.undoAction(a.id);
                    refresh();
                  }}
                >
                  Undo
                </button>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
